// materials.json → liltoon 近似 ShaderMaterial（M3）。
// 本体マテリアルとアウトライン（反転ハル）マテリアルを index ごとに構築する。
// 省略テクスチャスロットは既定テクスチャ（白 / フラット法線）で埋める。

import * as THREE from "three";
import type { FmMaterial, LoadedAvatar, TextureRef } from "../core/types.ts";
import {
  createLightingUniforms,
  createLiltoonMaterial,
  createOutlineMaterial,
  type LightingUniforms,
  type SlotTextures,
} from "./liltoon.ts";

/** 1x1 の単色データテクスチャ（0〜255） */
function solidTexture(r: number, g: number, b: number, a: number): THREE.DataTexture {
  const tex = new THREE.DataTexture(new Uint8Array([r, g, b, a]), 1, 1, THREE.RGBAFormat);
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** PNG バイト列 → THREE.Texture（Blob URL 経由で非同期デコード）。
 *  ready はデコード完了（失敗時も含む）で解決する。サムネイル撮影など
 *  「テクスチャが載った状態で1回だけ描画したい」場面で待つために使う */
function loadTexture(bytes: Uint8Array, srgb: boolean): { texture: THREE.Texture; dispose: () => void; ready: Promise<void> } {
  const blob = new Blob([bytes.slice().buffer as ArrayBuffer], { type: "image/png" });
  const url = URL.createObjectURL(blob);
  let resolveReady: () => void;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  const texture = new THREE.TextureLoader().load(
    url,
    () => resolveReady(),
    undefined,
    () => resolveReady(),
  );
  texture.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  texture.flipY = true;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 4;
  return { texture, dispose: () => URL.revokeObjectURL(url), ready };
}

export interface BuiltMaterials {
  /** materials.json の index → 本体マテリアル */
  byIndex: Map<number, THREE.Material>;
  /** materials.json の index → アウトラインマテリアル（無い場合は未登録） */
  outlineByIndex: Map<number, THREE.Material>;
  /** 全マテリアルで共有するライティング uniform */
  lighting: LightingUniforms;
  /** 全テクスチャのデコードが完了（失敗時も含む）したら解決する */
  ready: Promise<void>;
  dispose(): void;
}

export function buildMaterials(avatar: LoadedAvatar): BuiltMaterials {
  const byIndex = new Map<number, THREE.Material>();
  const outlineByIndex = new Map<number, THREE.Material>();
  const cleanups: Array<() => void> = [];
  const readyList: Promise<void>[] = [];
  const lighting = createLightingUniforms();

  // 既定テクスチャ（スロット省略時）。sRGB 変換不要な値なので NoColorSpace。
  const white = solidTexture(255, 255, 255, 255);
  const flatNormal = solidTexture(128, 128, 255, 255);
  cleanups.push(() => {
    white.dispose();
    flatNormal.dispose();
  });

  const resolve = (ref: TextureRef | undefined, fallback: THREE.Texture): THREE.Texture => {
    const bytes = ref ? avatar.textures.get(ref.file) : undefined;
    if (!ref || !bytes) return fallback;
    const { texture, dispose, ready } = loadTexture(bytes, ref.colorSpace === "sRGB");
    readyList.push(ready);
    cleanups.push(() => {
      texture.dispose();
      dispose();
    });
    return texture;
  };

  for (const m of avatar.materials.materials) {
    const tex: SlotTextures = {
      // mainTex / shadeTex は sRGB 指定を尊重、normalMap / outlineMask は数値扱い
      mainTex: resolve(m.textures.mainTex, white),
      shadeTex: resolve(m.textures.shadeTex, white),
      normalMap: resolve(m.textures.normalMap, flatNormal),
      outlineMask: resolve(m.textures.outlineMask, white),
    };

    byIndex.set(m.index, createLiltoonMaterial(m, tex, lighting));
    const outline = createOutlineMaterial(m, tex);
    if (outline) outlineByIndex.set(m.index, outline);
  }

  return {
    byIndex,
    outlineByIndex,
    lighting,
    ready: Promise.all(readyList).then(() => {}),
    dispose(): void {
      for (const mat of byIndex.values()) mat.dispose();
      for (const mat of outlineByIndex.values()) mat.dispose();
      for (const c of cleanups) c();
      byIndex.clear();
      outlineByIndex.clear();
    },
  };
}

/** shaderType が liltoon 以外のマテリアル名一覧（警告表示用） */
export function nonLiltoonMaterials(mats: FmMaterial[]): string[] {
  return mats.filter((m) => m.shaderType !== "liltoon").map((m) => m.name);
}
